'use client';
import cn from 'classnames';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

import './ApplicationReviewCards.style.scss';

import ReviewCard from '@/components/common/ReviewCard/ReviewCard';
import Text from '@/components/common/Text/Text';
import Info from 'public/img/icon/info.svg';
import { CACHE_TIME } from '@/const/cache';
import { DUE_NOW_DISPLAY } from '@/const/due_now';
import { RawEPCCCartResponse } from '@/types/Cart';
import { SignupState } from '@/types/Signup';
import { InsuranceProduct, SubscriptionProduct } from '@/types/Subscription';
import { displayTierName } from '@/util/product_client_safe';
import { getMembershipTaxCode } from '@/util/tax';

import type { ReferralCopyProps } from '@/components/common/SignUpApp/SignUpApp';
import { ApplicationFormValues } from './applicationValidation';

const USDFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
});

type ApplicationReviewCardsProps = {
  user: SignupState;
  values: ApplicationFormValues;
  previewCart?: RawEPCCCartResponse;
  /**
   * False while a tier/add-on/coverage change has been made but the
   * repriced cart hasn't come back yet. Defaults to `true` (not pending) —
   * a caller that hasn't wired the real flag through gets the old,
   * always-current-looking behavior rather than a permanently dimmed card.
   */
  previewCartUpdated?: boolean;
  /**
   * The catalogs a cart line's `product_id` is matched against to attribute
   * it to the base plan, an add-on, or an insurance level. Optional (and
   * defaulted to empty) because the fallback for "can't match anything" is
   * already the desired behavior — one combined Membership line, same as
   * before this breakdown existed.
   */
  subs?: SubscriptionProduct[];
  insuranceData?: InsuranceProduct[];
  /** Same referral message the membership summary shows, so the two match. */
  referralCopy?: ReferralCopyProps;
  /**
   * Opens the membership editor from this card. Mobile-only in practice — see
   * the `ReviewCard-edit` override in the stylesheet — since desktop already
   * shows that editor permanently in the right column.
   */
  onEditMembership?: () => void;
};

/**
 * The two cards above the consents and Submit: what the applicant is signing up
 * for, and what it costs.
 *
 * No pricing math is invented here. The membership figure and any promotion
 * discount come from the preview cart (`/api/cart/preview`, which prices the
 * real products); the tax rate comes from `/api/cart/tax` keyed on the shipping
 * address, exactly as the multi-page review step does. Due Now is the constant
 * `$0` — the card on file is authorized, never captured, until the application
 * is approved.
 */
function ApplicationReviewCards(props: ApplicationReviewCardsProps) {
  const {
    user,
    values,
    previewCart,
    previewCartUpdated = true,
    subs = [],
    insuranceData = [],
    referralCopy,
    onEditMembership
  } = props;

  const [taxRate, setTaxRate] = useState(0);
  const [billingInfoOpen, setBillingInfoOpen] = useState(false);
  const billingInfoRef = useRef<HTMLSpanElement>(null);

  /**
   * Closes on a tap/click anywhere outside, or Escape. Hover alone (as
   * `MemberSummaryPanel`'s tier tooltips use) has no real equivalent on
   * touch — tapping the icon opens it via focus, but nothing ever closed it
   * again, so it just sat open.
   */
  useEffect(() => {
    if (!billingInfoOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!billingInfoRef.current?.contains(event.target as Node)) {
        setBillingInfoOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setBillingInfoOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [billingInfoOpen]);

  const { address, aptUnitSuite, city, state, postalCode } = values;

  useEffect(() => {
    // No point asking for a rate before there is an address to key it on.
    if (!address || !city || !state || !postalCode) {
      return;
    }

    const controller = new AbortController();

    const getTaxRate = async () => {
      try {
        const response = await fetch('/api/cart/tax', {
          method: 'POST',
          next: { revalidate: CACHE_TIME.NO_CACHE },
          signal: controller.signal,
          body: JSON.stringify({
            street1: address,
            street2: aptUnitSuite || ' ',
            city,
            state,
            country: 'us',
            zip: postalCode,
            // Chicago addresses get the lease tax code so the estimate reflects the
            // 15% Personal Property Lease Transaction Tax, matching SignupStep5Form
            // and what the subscription is actually billed (VIVE-3582 / VIVE-3698).
            taxCode: getMembershipTaxCode({ city, state })
          })
        });

        const json = (await response.json()) as {
          success: boolean;
          data: number;
        };

        setTaxRate(json.data ?? 0);
      } catch (error) {
        if ((error as Error)?.name !== 'AbortError') {
          console.error(error);
        }
      }
    };

    getTaxRate();

    return () => controller.abort();
  }, [address, aptUnitSuite, city, state, postalCode]);

  const price = previewCart?.data?.meta.display_price;

  const membership = (price?.without_discount.amount ?? 0) / 100;
  const discount = (price?.discount.amount ?? 0) / 100;
  const subtotal = (price?.without_tax.amount ?? 0) / 100;
  const tax = subtotal * taxRate;

  /**
   * The preview cart prices the base plan, the add-on, and insurance as three
   * separate line items — `membership` above is just their sum. Match each
   * back to its cart line by product id so Billing Summary can show what the
   * member is actually paying for, not one folded-together figure.
   */
  const cartItems = previewCart?.included?.items;

  const priceForProduct = (productId?: string) => {
    const item = productId
      ? cartItems?.find((i) => i.product_id === productId)
      : undefined;
    return item
      ? item.meta.display_price.without_tax.value.amount / 100
      : undefined;
  };

  const addonProductId =
    user.addon_subscription && user.addon_subscription !== 'none'
      ? subs.find((s) => s.slug === user.addon_subscription)?.productId
      : undefined;
  const insuranceProductId = insuranceData.find(
    (i) => i.slug === user.insurance_level
  )?.id;

  const basePrice = priceForProduct(user.plan?.productId);
  const addonPrice = priceForProduct(addonProductId);
  const insurancePrice = priceForProduct(insuranceProductId);

  // Only break the total apart once every line it should include actually
  // resolved — a partial breakdown that doesn't add up to `membership` would
  // be worse than the one combined figure it replaces.
  const canBreakDownMembership =
    basePrice !== undefined &&
    (!addonProductId || addonPrice !== undefined) &&
    (!insuranceProductId || insurancePrice !== undefined);

  const tier = [
    displayTierName(user.plan?.name ?? ''),
    user.addon_subscription === 'premier' ? 'Premier add-on' : null
  ]
    .filter(Boolean)
    .join(', ');

  const coverage = user.insurance_level === 'premium' ? 'Premium' : 'Standard';
  const referral = user.referralCode?.trim();

  return (
    <div className='ApplicationReviewCards'>
      <ReviewCard
        title='Membership Details'
        testId='review-membership-details'
        onEdit={onEditMembership}
      >
        <dl className='ApplicationReviewCards-details'>
          <div className='ApplicationReviewCards-detail'>
            <dt>
              <Text variant='title-xxs-500-upper'>Tier:</Text>
            </dt>
            <dd data-testid='review-tier'>{tier}</dd>
          </div>
          <div className='ApplicationReviewCards-detail'>
            <dt>
              <Text variant='title-xxs-500-upper'>Coverage:</Text>
            </dt>
            <dd data-testid='review-coverage'>{coverage}</dd>
          </div>
          {referral && (
            /**
             * The referral row wraps: the code sits beside its label, and the
             * caveat starts a new line flush with the label — not indented into
             * the value column, which is where it lands if it stays inside the
             * `dd`'s flow.
             */
            <div className='ApplicationReviewCards-detail ApplicationReviewCards-detail--stacked'>
              <dt>
                <Text variant='title-xxs-500-upper'>Referral:</Text>
              </dt>
              <dd data-testid='review-referral'>{referral}</dd>
              {/* 13px regular under the code, per the design's referral block. */}
              <Text
                variant='text-s'
                className='ApplicationReviewCards-note'
              >
                {referralCopy?.copy ?? 'Applied after acceptance'}
              </Text>
            </div>
          )}
        </dl>
      </ReviewCard>

      <ReviewCard
        title={
          <>
            Billing Summary
            {/*
              Click/tap toggles (works for touch and keyboard Enter/Space
              alike); hover is layered on for a mouse. The dismiss side is a
              document-level outside-click/Escape listener, above — hover
              alone (as `MemberSummaryPanel`'s tier tooltips use) has nothing
              to close it again on a touch device once a tap opened it.
            */}
            <span
              className='ApplicationReviewCards-info'
              ref={billingInfoRef}
            >
              <button
                type='button'
                className='ApplicationReviewCards-info-btn cursor'
                aria-label='About billing summary'
                onClick={() => setBillingInfoOpen((open) => !open)}
                onMouseEnter={() => setBillingInfoOpen(true)}
                onMouseLeave={() => setBillingInfoOpen(false)}
              >
                <Image
                  src={Info}
                  alt=''
                  width={16}
                  height={16}
                />
              </button>
              {billingInfoOpen && (
                <div
                  className='ApplicationReviewCards-tip'
                  role='tooltip'
                >
                  <Text
                    variant='title-xxs-500'
                    className='ApplicationReviewCards-tip-title'
                  >
                    Since $0 is due now, when will I be charged?
                  </Text>
                  <Text variant='text-s'>
                    Your billing cycle does not start until your membership
                    application has been reviewed by our team and accepted.
                  </Text>
                  <Text
                    variant='title-xxs-500'
                    className='ApplicationReviewCards-tip-title'
                  >
                    How is sales tax calculated?
                  </Text>
                  <Text variant='text-s'>
                    Sales tax is estimated based on your shipping address
                    inputted at the time of application.
                  </Text>
                </div>
              )}
            </span>
          </>
        }
        testId='review-billing-summary'
        footer={
          /*
            Always $0: the payment method is authorized at signup (a
            manual-capture hold that is never captured), and billing does not
            start until the application is approved and a first item is
            borrowed.
          */
          <>
            <Text variant='title-xxs-700-upper'>Due now:</Text>
            <span data-testid='review-due-now'>{DUE_NOW_DISPLAY}</span>
          </>
        }
      >
        <dl
          className={cn('ApplicationReviewCards-lines', {
            pending: !previewCartUpdated
          })}
        >
          <div className='ApplicationReviewCards-line'>
            <dt>
              <Text variant='title-xxs-500-upper'>Membership:</Text>
            </dt>
            <dd data-testid='review-membership-amount'>
              {USDFormatter.format(
                canBreakDownMembership ? (basePrice as number) : membership
              )}
            </dd>
          </div>

          {/*
            Standard coverage and "no add-on" are both real, valid choices —
            they just happen to cost nothing. A "$0.00" line for either reads
            as a bug rather than a deliberate free option, so a free line is
            left out entirely rather than shown at zero.
          */}
          {canBreakDownMembership && addonProductId && !!addonPrice && (
            <div className='ApplicationReviewCards-line'>
              <dt>
                <Text variant='title-xxs-500-upper'>Add-on:</Text>
              </dt>
              <dd data-testid='review-addon-amount'>
                {USDFormatter.format(addonPrice as number)}
              </dd>
            </div>
          )}

          {canBreakDownMembership && insuranceProductId && !!insurancePrice && (
            <div className='ApplicationReviewCards-line'>
              <dt>
                <Text variant='title-xxs-500-upper'>Coverage:</Text>
              </dt>
              <dd data-testid='review-coverage-amount'>
                {USDFormatter.format(insurancePrice as number)}
              </dd>
            </div>
          )}

          {discount > 0 && (
            <div className='ApplicationReviewCards-line ApplicationReviewCards-line--credit'>
              <dt>
                <Text variant='title-xxs-500-upper'>Discount:</Text>
              </dt>
              <dd data-testid='review-discount'>
                {USDFormatter.format(discount)}
              </dd>
            </div>
          )}

          <div className='ApplicationReviewCards-line'>
            <dt>
              <Text variant='title-xxs-500-upper'>Estimated sales tax:</Text>
            </dt>
            <dd data-testid='review-tax'>{USDFormatter.format(tax)}</dd>
          </div>

          <div className='ApplicationReviewCards-line ApplicationReviewCards-line--total'>
            <dt>
              <Text variant='title-xxs-700-upper'>First month total:</Text>
            </dt>
            <dd data-testid='review-first-month-total'>
              {USDFormatter.format(subtotal + tax)}
            </dd>
          </div>
        </dl>
        <Text
          variant='text-s'
          className='ApplicationReviewCards-cycle-note'
        >
          Your billing cycle will begin once your membership application has
          been accepted.
        </Text>
      </ReviewCard>
    </div>
  );
}

export default ApplicationReviewCards;
